import { Select, type TypeSelectOption, dialogService } from '@thefarmersfront/kitchen';

import type { ColDef, GetRowIdParams, GridOptions, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import { CheckboxCell, NumberInputCell, TableHeaderWithTooltipIcon } from '~/components/common';

import { formatNumber, getSelectPlacement } from '~/utils/common';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type { TypeReturnRegisterSecondStepValues, TypeReturnRegisterSecondStepValuesState } from '~/types/claim.types';

type TypeReturnOrderItem = TypeReturnRegisterSecondStepValues['returnOrderItems'][number];

/**
 * [반품 신청/조회] 반품 신청 등록 모달 > 반품 정보 입력 > '반품 상품' 테이블 컬럼 정의
 */
const getClaimReturnRegisterReturnItemsColumnDefs = ({
  data: tableData,
  dataState,
  selectOptions,
  onChange,
  onChangeState,
}: {
  data: TypeReturnOrderItem[];
  dataState: TypeReturnRegisterSecondStepValuesState['returnOrderItems'];
  selectOptions: TypeSelectOption<string>[];
  onChange: (value: Partial<TypeReturnRegisterSecondStepValues>) => void;
  onChangeState: (value: Partial<TypeReturnRegisterSecondStepValuesState>) => void;
}) => {
  const gridOptions: GridOptions = {
    suppressScrollOnNewData: true,
    getRowId: (params: GetRowIdParams<TypeReturnOrderItem>) => `${params.data.itemName}-${params.data.itemId}`,
  };

  const columnDefs: ColDef<TypeReturnOrderItem>[] = [
    {
      field: 'itemId',
      headerName: '상품코드',
      width: 130,
    },
    {
      field: 'itemName',
      headerName: '상품명',
      minWidth: 365,
      flex: 1,
    },
    {
      field: 'isReturn',
      headerName: '반품여부',
      width: 76,
      minWidth: 76,
      cellRenderer: CheckboxCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeReturnOrderItem, TypeReturnOrderItem['isReturn']>) => ({
        label: '반품',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          onChange({
            returnOrderItems: tableData.map((item) =>
              item.itemId === data?.itemId
                ? { ...item, isReturn: e.target.checked, returnQuantity: '', returnReason: null }
                : item
            ),
          });
          onChangeState({
            returnOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId
                ? { ...item, returnQuantity: DEFAULT_INPUT_STATUS, returnReason: DEFAULT_INPUT_STATUS }
                : item
            ),
          });
        },
      }),
    },
    {
      field: 'quantity',
      headerName: '구매수량',
      width: 79,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeReturnOrderItem, TypeReturnOrderItem['quantity']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'returnQuantity',
      headerName: '반품수량',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '필수 입력입니다.',
        tooltipMaxWidth: '103px',
      },
      width: 100,
      minWidth: 100,
      cellRenderer: NumberInputCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<TypeReturnOrderItem, TypeReturnOrderItem['returnQuantity']>) => ({
        disabled: !data?.isReturn,
        status: dataState.find((item) => item.itemId === data?.itemId)?.returnQuantity.status,
        validationMessage: dataState.find((item) => item.itemId === data?.itemId)?.returnQuantity.message,
        onChange: (value: string) => {
          onChange({
            returnOrderItems: tableData.map((item) =>
              item.itemId === data?.itemId ? { ...item, returnQuantity: value } : item
            ),
          });
          onChangeState({
            returnOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId ? { ...item, returnQuantity: DEFAULT_INPUT_STATUS } : item
            ),
          });
        },
        onBlur: (value: string) => {
          const numberValue = Number(value.trim());
          const quantity = tableData.find((item) => item.itemId === data?.itemId)?.quantity ?? 0;

          if (numberValue === 0) {
            onChange({
              returnOrderItems: tableData.map((item) =>
                item.itemId === data?.itemId ? { ...item, returnQuantity: '' } : item
              ),
            });
            return;
          }

          if (numberValue > quantity) {
            onChangeState({
              returnOrderItems: dataState.map((item) =>
                item.itemId === data?.itemId ? { ...item, returnQuantity: { status: 'error' } } : item
              ),
            });

            dialogService.addDialog({
              children: '반품수량은 구매수량보다 같거나 적은 수량만 입력할 수 있습니다.',
              onConfirm: () => {
                onChange({
                  returnOrderItems: tableData.map((item) =>
                    item.itemId === data?.itemId ? { ...item, returnQuantity: '' } : item
                  ),
                });
                onChangeState({
                  returnOrderItems: dataState.map((item) =>
                    item.itemId === data?.itemId ? { ...item, returnQuantity: DEFAULT_INPUT_STATUS } : item
                  ),
                });
              },
            });
          }
        },
        onFocus: () =>
          onChangeState({
            returnOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId ? { ...item, returnQuantity: DEFAULT_INPUT_STATUS } : item
            ),
          }),
      }),
    },
    {
      headerName: '반품사유',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '필수 선택입니다.',
        tooltipMaxWidth: '103px',
      },
      width: 147,
      minWidth: 147,
      cellRenderer: Select,
      cellRendererParams: ({
        node,
        data,
      }: ICellRendererParams<TypeReturnOrderItem, TypeReturnOrderItem['returnReason']>) => ({
        size: 'tiny',
        placeholder: '선택',
        dropdownPortalTarget: document.body,
        shouldCloseOnNotInView: true,
        dropdownPlacement: getSelectPlacement(node.rowIndex, tableData.length, { thresholdRow: 5 }),
        disabled: !data?.isReturn,
        options: selectOptions,
        status: dataState.find((item) => item.itemId === data?.itemId)?.returnReason.status,
        validationMessage: dataState.find((item) => item.itemId === data?.itemId)?.returnReason.message,
        value: data?.returnReason ?? null,
        onChange: (_: unknown, value: TypeSelectOption<string>) => {
          onChange({
            returnOrderItems: tableData.map((item) =>
              item.itemId === data?.itemId ? { ...item, returnReason: value } : item
            ),
          });
          onChangeState({
            returnOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId ? { ...item, returnReason: DEFAULT_INPUT_STATUS } : item
            ),
          });
        },
      }),
    },
  ];

  return { columnDefs, gridOptions };
};

export default getClaimReturnRegisterReturnItemsColumnDefs;
